import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSyncAlt } from "@fortawesome/free-solid-svg-icons";
import "./PainelInstancias.css";

function PainelInstancias() {
  const [instancias, setInstancias] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [usuarioSelecionado, setUsuarioSelecionado] = useState("");
  const [carregando, setCarregando] = useState(false);
  const [criando, setCriando] = useState(false);
  const [instanciaEmProcesso, setInstanciaEmProcesso] = useState(null);
  const [acaoEmProgresso, setAcaoEmProgresso] = useState(null);

  // Filtros
  const [filtroNome, setFiltroNome] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("todos"); // todos | conectado | desconectado

  // Helpers
  const normalize = (s) =>
    (s || "")
      .toString()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();

  const formatarDataBrasileira = (dataIso) => {
    if (!dataIso) return "—";
    const data = new Date(dataIso);
    return data
      .toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
      .replace(",", " às");
  };

  const traduzirStatusPagamento = (status) => {
    switch (status) {
      case "PAID":
        return "PAGO";
      case "TRIAL":
        return "TESTE";
      case "UNPAID":
        return "NÃO PAGO";
      case "CANCELED":
      case "CANCELLED":
        return "CANCELADO";
      default:
        return status || "pendente";
    }
  };

  const isCanceladaFromStatus = (paymentStatus) =>
    paymentStatus === "CANCELED" ||
    paymentStatus === "CANCELLED" ||
    paymentStatus === "UNPAID";

  // Carregamentos
  const carregarInstancias = async () => {
    setCarregando(true);
    try {
      const res = await axios.get("/crm-wly/api/whatsapp/getInstancias.php");
      setInstancias(res.data.instancias || []);
    } catch (err) {
      console.error("Erro ao carregar instâncias", err);
    } finally {
      setCarregando(false);
    }
  };

  const carregarUsuarios = async () => {
    try {
      const res = await axios.get("/crm-wly/listagem_usuarios.php");
      setUsuarios(res.data.usuarios || []);
    } catch (err) {
      console.error("Erro ao carregar usuários", err);
    }
  };

  useEffect(() => {
    carregarInstancias();
  }, []);

  // Ações
  const criarInstancia = async () => {
    if (!usuarioSelecionado) return;
    setCriando(true);
    try {
      await axios.post(
        "/crm-wly/api/whatsapp/criar_instancia.php",
        { usuario_id: usuarioSelecionado },
        { withCredentials: true }
      );
      alert("Instância criada com sucesso!");
      setShowPopup(false);
      setUsuarioSelecionado("");
      await carregarInstancias();
    } catch (err) {
      alert("Erro ao criar instância");
      console.error(err);
    } finally {
      setCriando(false);
    }
  };

  const assinarInstancia = async (instance_id, token) => {
    setInstanciaEmProcesso(instance_id);
    setAcaoEmProgresso("assinar");
    try {
      const formData = new FormData();
      formData.append("instance_id", instance_id);
      formData.append("token", token);
      await axios.post("/crm-wly/api/whatsapp/assinar_instancia.php", formData);
      alert("Instância assinada com sucesso!");
      await carregarInstancias();
    } catch (err) {
      alert("Erro ao assinar instância");
      console.error(err);
    } finally {
      setInstanciaEmProcesso(null);
      setAcaoEmProgresso(null);
    }
  };

  const cancelarInstancia = async (instance_id, token) => {
    setInstanciaEmProcesso(instance_id);
    setAcaoEmProgresso("cancelar");
    try {
      const formData = new FormData();
      formData.append("instance_id", instance_id);
      formData.append("token", token);
      await axios.post("/crm-wly/api/whatsapp/cancelar_instancia.php", formData);
      alert("Instância cancelada com sucesso!");
      await carregarInstancias();
    } catch (err) {
      alert("Erro ao cancelar instância");
      console.error(err);
    } finally {
      setInstanciaEmProcesso(null);
      setAcaoEmProgresso(null);
    }
  };

  // Totais e lista filtrada
  const totalConectadas = useMemo(
    () => instancias.filter((i) => !!i.whatsappConnected).length,
    [instancias]
  );

  const totalDesconectadas = useMemo(
    () => instancias.filter((i) => !i.whatsappConnected).length,
    [instancias]
  );

  const instanciasFiltradas = useMemo(() => {
    const f = normalize(filtroNome);
    let base = instancias;

    if (f) {
      base = base.filter((inst) => normalize(inst.usuario_nome).includes(f));
    }

    if (filtroStatus === "conectado") {
      base = base.filter((inst) => !!inst.whatsappConnected);
    } else if (filtroStatus === "desconectado") {
      base = base.filter((inst) => !inst.whatsappConnected);
    }

    return base;
  }, [instancias, filtroNome, filtroStatus]);

  const totalInstancias = instancias.length;
  const totalFiltradas = instanciasFiltradas.length;

  return (
    <>
      <h2
        style={{ borderBottom: "none" }}
        className="painel-instancias-titulo-principal"
      >
        Gerenciamento de Instâncias Z-API
      </h2>

      <div className="painel-instancias-wrapper">
        {/* KPIs topo */}
        <div className="painel-instancias-kpis">
          <button
            className={`kpi-card ${filtroStatus === "todos" ? "ativo" : ""}`}
            onClick={() => setFiltroStatus("todos")}
            title="Mostrar todas as instâncias"
          >
            <span className="kpi-titulo">Todas</span>
            <span className="kpi-valor">{totalInstancias}</span>
          </button>

          <button
            className={`kpi-card conectado ${
              filtroStatus === "conectado" ? "ativo" : ""
            }`}
            onClick={() => setFiltroStatus("conectado")}
            title="Filtrar por conectadas"
          >
            <span className="kpi-dot" />
            <span className="kpi-titulo">Conectadas</span>
            <span className="kpi-valor">{totalConectadas}</span>
          </button>

          <button
            className={`kpi-card desconectado ${
              filtroStatus === "desconectado" ? "ativo" : ""
            }`}
            onClick={() => setFiltroStatus("desconectado")}
            title="Filtrar por desconectadas"
          >
            <span className="kpi-dot" />
            <span className="kpi-titulo">Desconectadas</span>
            <span className="kpi-valor">{totalDesconectadas}</span>
          </button>

          {filtroStatus !== "todos" && (
            <button
              className="kpi-clear"
              onClick={() => setFiltroStatus("todos")}
              title="Limpar filtro"
            >
              Limpar filtro
            </button>
          )}
        </div>

        {/* Barra de ações */}
        <div className="painel-instancias-botoes-topo">
          <button
            onClick={() => {
              setShowPopup(true);
              carregarUsuarios();
            }}
            className="painel-instancias-botao-criar"
          >
            <span className="painel-instancias-botao-icono">+</span> Criar nova
            instância
          </button>

          <button
            onClick={carregarInstancias}
            className="painel-instancias-botao-atualizar"
            title="Atualizar lista de instâncias"
          >
            <FontAwesomeIcon icon={faSyncAlt} />
          </button>

          <div className="painel-instancias-right">
            <div className="painel-instancias-filtro">
              <input
                type="text"
                placeholder="Filtrar por nome do usuário…"
                value={filtroNome}
                onChange={(e) => setFiltroNome(e.target.value)}
                className="painel-instancias-input-filtro"
              />
              {filtroNome && (
                <button
                  className="painel-instancias-filtro-limpar"
                  onClick={() => setFiltroNome("")}
                  title="Limpar filtro"
                >
                  ×
                </button>
              )}
            </div>

            <div className="painel-instancias-contador">
              <span className="badge">
                Total: <strong>{totalInstancias}</strong>
              </span>
              <span className="badge secundario">
                Exibindo: <strong>{totalFiltradas}</strong>
              </span>
              {filtroStatus !== "todos" && (
                <span className="badge status">
                  Filtro: <strong>{filtroStatus}</strong>
                </span>
              )}
            </div>
          </div>
        </div>

        {carregando ? (
          <p className="painel-instancias-loading">Carregando instâncias...</p>
        ) : (
          <div className="painel-instancias-cards-container">
            {instanciasFiltradas.map((inst) => {
              const isCancelada = isCanceladaFromStatus(inst.paymentStatus);

              const cardClass = isCancelada
                ? "instancia-cancelada"
                : inst.whatsappConnected
                ? "instancia-conectada"
                : "instancia-desconectada";

              const statusConexaoClass = isCancelada
                ? "cancelado"
                : inst.whatsappConnected
                ? "conectado"
                : "desconectado";

              const statusConexaoTexto = isCancelada
                ? "CANCELADO"
                : inst.whatsappConnected
                ? "CONECTADO"
                : "DESCONECTADO";

              return (
                <div key={inst.id} className={`painel-instancias-card ${cardClass}`}>
                  <div className="painel-instancias-usuario">
                    <p>
                      <strong>Usuário:</strong> {inst.usuario_nome || "—"}
                    </p>
                    <p className="painel-instancias-id-usuario">
                      ID: {inst.usuario_id || "—"}
                    </p>
                  </div>

                  <p>
                    <strong>Instance ID:</strong> {inst.id}
                  </p>

                  <p className="painel-data-criacao">
                    <strong>Criado em:</strong>{" "}
                    {formatarDataBrasileira(inst.created)}
                  </p>

                  <p>
                    <strong>Status de Pagamento:</strong>{" "}
                    <span
                      className={
                        isCancelada
                          ? "painel-status-cancelado"
                          : inst.paymentStatus === "PAID"
                          ? "painel-status-pago"
                          : inst.paymentStatus === "TRIAL"
                          ? "painel-status-trial"
                          : inst.paymentStatus === "UNPAID"
                          ? "painel-status-nao-pago"
                          : ""
                      }
                    >
                      {isCancelada ? "CANCELADO" : traduzirStatusPagamento(inst.paymentStatus)}
                    </span>
                  </p>

                  <div className={`painel-status-conexao ${statusConexaoClass}`}>
                    {statusConexaoTexto}
                  </div>

                  <div className="painel-instancias-card-botoes">
                    {inst.paymentStatus !== "PAID" && (
                      <button
                        onClick={() => assinarInstancia(inst.id, inst.token)}
                        className="painel-instancias-btn-assinar"
                        disabled={
                          instanciaEmProcesso === inst.id &&
                          acaoEmProgresso === "assinar"
                        }
                      >
                        {instanciaEmProcesso === inst.id &&
                        acaoEmProgresso === "assinar"
                          ? "Assinando..."
                          : "Assinar"}
                      </button>
                    )}

                    <button
                      onClick={() => cancelarInstancia(inst.id, inst.token)}
                      className="painel-instancias-btn-cancelar"
                      disabled={
                        instanciaEmProcesso === inst.id &&
                        acaoEmProgresso === "cancelar"
                      }
                    >
                      {instanciaEmProcesso === inst.id &&
                      acaoEmProgresso === "cancelar"
                        ? "Cancelando..."
                        : "Cancelar"}
                    </button>
                  </div>
                </div>
              );
            })}

            {!carregando && instancias.length > 0 && instanciasFiltradas.length === 0 && (
              <p className="painel-instancias-sem-resultado">
                Nenhuma instância encontrada para “{filtroNome}”
                {filtroStatus !== "todos" ? ` com filtro ${filtroStatus}` : ""}.
              </p>
            )}
          </div>
        )}

        {/* POPUP DE CRIAÇÃO */}
        {showPopup && (
          <div className="painel-instancias-popup-overlay">
            <div className="painel-instancias-popup">
              <h3>Selecionar cliente</h3>
              <select
                value={usuarioSelecionado}
                onChange={(e) => setUsuarioSelecionado(e.target.value)}
                className="painel-instancias-select"
              >
                <option value="">Selecione um usuário</option>
                {usuarios.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.nome || `Usuário ${u.id}`}
                  </option>
                ))}
              </select>
              <div className="painel-instancias-popup-botoes">
                <button
                  onClick={criarInstancia}
                  className="painel-popup-btn-confirmar"
                  disabled={criando}
                >
                  {criando ? "Criando..." : "Criar"}
                </button>
                <button
                  onClick={() => setShowPopup(false)}
                  className="painel-popup-btn-cancelar"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default PainelInstancias;
